import React from 'react';

import { useQueryMobileAppSubmissionDetailsHook } from '@fd/customHooks/useQueryMobileAppsSubmissionDetailsHook';
import { MobileAppsSubmission, MobileAppsSubmissionStatus } from '@flipdish/api-client-typescript';
import { makeStyles, Theme } from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { getTranslate, Translate } from 'react-localize-redux';
import { useMutation } from 'react-query';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { MessageBanner } from '@fd/ui/atoms';
import PageLayout from '@fd/ui/Layout';

import {
  closeNotifySaving,
  notifyError,
  NotifyProps,
  notifySaved,
  notifySaving,
} from '../../../../../layouts/Notify/actions';
import { flagService } from '../../../../../services';
import { SubmissionForm } from '../../components/SubmissionForm';
import { mobileAppsService } from '../../mobileApps.services';

const useStyles = makeStyles((theme: Theme) => ({
  rejectedBanner: {
    marginBottom: theme.spacing(3),
  },
}));

const AppSubmission = (props: MappedState & MappedDispatch) => {
  const {
    appId,
    isMobileAppsOn,
    appName,
    translate,
    dispatchCloseNotifySaving,
    dispatchNotifyError,
    dispatchNotifySaved,
    dispatchNotifySaving,
  } = props;
  const history = useHistory();
  const classes = useStyles({});

  const { data, isLoading, isFetching } = useQueryMobileAppSubmissionDetailsHook(
    appId,
    isMobileAppsOn || false
  );

  const { mutateAsync: createSubmission } = useMutation(
    (mobileApp: MobileAppsSubmission) => {
      dispatchNotifySaving();
      return mobileAppsService.saveSubmissionDetails(appId, mobileApp);
    },
    {
      onSuccess: () => {
        dispatchCloseNotifySaving();
        dispatchNotifySaved();
        history.push(`/${appId}/sales-channels/mobile-apps`);
      },
      onError: () => {
        dispatchCloseNotifySaving();
        dispatchNotifyError({ message: 'Error_please_try_again_later', translate: true });
      },
    }
  );

  const { mutateAsync: updateSubmission } = useMutation(
    (mobileApp: MobileAppsSubmission) => {
      dispatchNotifySaving();
      const updatedAppSubmission = {
        ...mobileApp,
        SubmissionId: data?.Data?.MobileAppsSubmissionId,
      };
      return mobileAppsService.updateSubmissionDetails(appId, updatedAppSubmission);
    },
    {
      onSuccess: () => {
        dispatchCloseNotifySaving();
        dispatchNotifySaved();
        history.push(`/${appId}/sales-channels/mobile-apps`);
      },
      onError: () => {
        dispatchCloseNotifySaving();
        dispatchNotifyError({ message: 'Error_please_try_again_later', translate: true });
      },
    }
  );

  const isRejected = () => {
    const array = data?.Data?.Status || [];
    return (
      array.findIndex(
        (status) => status.UpdateStatus === MobileAppsSubmissionStatus.UpdateStatusEnum.Rejected
      ) !== -1
    );
  };

  return isMobileAppsOn ? (
    <PageLayout
      documentTitle="Android_and_iOS_submission"
      strictToParent={true}
      title={<Translate id="Android_and_iOS_submission" />}
      toParent={`/${appId}/sales-channels/mobile-apps`}
    >
      {isRejected() && (
        <MessageBanner
          className={classes.rejectedBanner}
          fdKey="rejected-message"
          icon={<InfoOutlined />}
          title="Your app submission doesn't follow the guidlines from Google's play store."
          rounded
          variant="error"
        />
      )}
      <SubmissionForm
        appId={appId}
        handleSave={data?.Data?.MobileAppsSubmissionId !== 0 ? updateSubmission : createSubmission}
        //most values auto created on account creation and logourl links in the BE after image upload
        mobileApp={data?.Data?.MobileAppsSubmissionId !== 0 ? data?.Data : {}}
        translate={translate}
        appName={appName}
        loading={isLoading || isFetching}
      />
    </PageLayout>
  ) : null;
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  dispatchCloseNotifySaving: () => dispatch(closeNotifySaving()),
  dispatchNotifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  dispatchNotifySaving: () => dispatch(notifySaving({ persist: true })),
  dispatchNotifySaved: () => dispatch(notifySaved()),
});

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => {
  return {
    appName: state.currentApp.Name,
    appId: state.currentApp.AppId as string,
    isMobileAppsOn: flagService.isFlagOn(state, 'mobileApps'),
    translate: getTranslate(state.locale),
  };
};

const EnhancedComponent = connect(mapStateToProps, mapDispatchToProps)(AppSubmission);
export { EnhancedComponent as AppSubmission };
